import React, { useState, useMemo, useEffect, useRef } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import './EGfrApp.scss';

interface TooltipProps {
    message: string;
    show: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({ message, show }) => {
    if (!show) return null;
    return <div className="tooltip-balloon" dangerouslySetInnerHTML={{ __html: message }} />;
};

interface EGfrAppProps {
    embedded?: boolean;
    onApplyResult?: (value: string) => void;
}

const EGfrApp: React.FC<EGfrAppProps> = ({
    embedded = false,
    onApplyResult,
}) => {
    const LOCAL_STORAGE_KEY = 'apps.egfr.meteringMethod';
    const FORMULA_STORAGE_KEY = 'apps.egfr.formula';
    const [formula, setFormula] = useState(
        () => localStorage.getItem(FORMULA_STORAGE_KEY) || 'schwartz'
    );
    const [creatinineMethod, setCreatinineMethod] = useState(
        () => localStorage.getItem(LOCAL_STORAGE_KEY) || 'enzymatic'
    );
    const [height, setHeight] = useState('');
    const [creatinine, setCreatinine] = useState('');
    const [age, setAge] = useState('');
    const [gender, setGender] = useState<'male' | 'female'>('male');
    const [jaffeK, setJaffeK] = useState('0.33');
    const [result, setResult] = useState<{ value: string; formula: string } | null>(null);
    const [showInfo, setShowInfo] = useState({ method: false, formula: false });
    const [ageError, setAgeError] = useState<string | null>(null);
    const calculateButtonRef = useRef<HTMLButtonElement>(null);
    const ageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_KEY, creatinineMethod);
    }, [creatinineMethod]);

    useEffect(() => {
        localStorage.setItem(FORMULA_STORAGE_KEY, formula);
    }, [formula]);

    // Close tooltips when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            // Check if click is outside tooltip and not on an info icon
            if (!target.closest('.tooltip-balloon') && !target.closest('.info-icon')) {
                setShowInfo({ method: false, formula: false });
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

	useEffect(() => {
    return () => {
			if (ageTimerRef.current) {
				clearTimeout(ageTimerRef.current);
			}
    	};
	}, []);

    const K_ENZYMATIC = 0.413;

    // Auto-select formula based on age
    const autoSelectFormula = (ageNum: number) => {
        if (ageNum <= 0) return;

        if (ageNum < 1) {
            setFormula('schwartz');
        } else if (ageNum >= 1 && ageNum < 18) {
            // Recommend CKiD if enzymatic method, otherwise Schwartz
            if (creatinineMethod === 'enzymatic') {
                setFormula('ckid');
            } else {
                setFormula('schwartz');
            }
        } else {
            setFormula('ckd-epi');
        }
    };

    const calculateCKiDK = (ageNum: number, isMale: boolean): number => {
        if (ageNum >= 1 && ageNum < 12) {
            const baseK = isMale ? 39.0 : 36.1;
            const exponent = 1.008;
            return baseK * Math.pow(exponent, ageNum - 12);
        } else if (ageNum >= 12 && ageNum < 18) {
            const baseK = isMale ? 39.0 : 36.1;
            const exponent = isMale ? 1.045 : 1.023;
            return baseK * Math.pow(exponent, ageNum - 12);
        } else if (ageNum >= 18 && ageNum <= 25) {
            return isMale ? 50.8 : 41.4;
        }
        return 0;
    };

    const calculateCKDEPI = (creatinineNum: number, ageNum: number, isMale: boolean): number => {
        let A: number, B: number;

        if (isMale) {
            if (creatinineNum <= 0.9) {
                A = 0.9;
                B = -0.302;
            } else {
                A = 0.9;
                B = -1.2;
            }
        } else {
            if (creatinineNum <= 0.7) {
                A = 0.7;
                B = -0.241;
            } else {
                A = 0.7;
                B = -1.2;
            }
        }

        const eGFR = 142 * Math.pow(creatinineNum / A, B) * Math.pow(0.9938, ageNum) * (isMale ? 1 : 1.012);
        return eGFR;
    };

    const kValue = useMemo(() => {
        if (creatinineMethod === 'enzymatic') {
            return K_ENZYMATIC;
        }
        return parseFloat(jaffeK);
    }, [creatinineMethod, jaffeK]);

    const resetResult = () => {
        if (result) {
            setResult(null);
        }
    };

    const handleAgeChange = (value: string) => {
        setAge(value);
        setAgeError(null);
        resetResult();

        // Clear existing timer
        if (ageTimerRef.current) {
            clearTimeout(ageTimerRef.current);
        }

        // Set new timer for auto-formula selection (1.5 seconds)
        if (value) {
            ageTimerRef.current = setTimeout(() => {
                const ageNum = parseFloat(value);
                if (!isNaN(ageNum) && ageNum > 0) {
                    autoSelectFormula(ageNum);
                }
            }, 1500);
        }
    };

    const handleAgeBlur = () => {
        // Clear timer on blur and immediately apply auto-selection
        if (ageTimerRef.current) {
            clearTimeout(ageTimerRef.current);
        }

        if (age) {
            const ageNum = parseFloat(age);
            if (!isNaN(ageNum) && ageNum > 0) {
                autoSelectFormula(ageNum);
            }
        }
    };

    const calculateEGFR = () => {
        const heightNum = parseFloat(height);
        const creatinineNum = parseFloat(creatinine);
        const ageNum = parseFloat(age);
        const isMale = gender === 'male';

        // Clear previous age error
        setAgeError(null);

        if (formula === 'schwartz') {
            if (heightNum > 0 && creatinineNum > 0) {
                const eGFR = (kValue * heightNum) / creatinineNum;
                setResult({
                    value: eGFR.toFixed(2),
                    formula: `(${height} * ${kValue.toFixed(3)} / ${creatinine})`
                });
                if (!embedded) {
                    setTimeout(() => {
                        calculateButtonRef.current?.scrollIntoView({ behavior: 'smooth' });
                    }, 300);
                }
            } else {
                setResult(null);
            }
        } else if (formula === 'ckid') {
            if (heightNum > 0 && creatinineNum > 0 && ageNum > 0) {
                const heightM = heightNum / 100;
                const kCKiD = calculateCKiDK(ageNum, isMale);
                if (kCKiD === 0) {
                    setAgeError('נוסחת CKiD U25 מתאימה רק לגילאי 1-25 שנים');
                    setResult(null);
                    return;
                }
                const eGFR = kCKiD * (heightM / creatinineNum);
                setResult({
                    value: eGFR.toFixed(2),
                    formula: `(${kCKiD.toFixed(2)} * ${heightM.toFixed(2)} / ${creatinine})`
                });
                if (!embedded) {
                    setTimeout(() => {
                        calculateButtonRef.current?.scrollIntoView({ behavior: 'smooth' });
                    }, 300);
                }
            } else {
                setResult(null);
            }
        } else if (formula === 'ckd-epi') {
            if (creatinineNum > 0 && ageNum > 0) {
                const eGFR = calculateCKDEPI(creatinineNum, ageNum, isMale);
                setResult({
                    value: eGFR.toFixed(2),
                    formula: 'CKD-EPI'
                });
                if (!embedded) {
                    setTimeout(() => {
                        calculateButtonRef.current?.scrollIntoView({ behavior: 'smooth' });
                    }, 300);
                }
            } else {
                setResult(null);
            }
        }
    };

    return (
        <div className="e-gfr-app">
            {!embedded && (
				<div className="header">
					<h1>מחשבון eGFR - תפקוד כלייתי</h1>
					<div className="header-description">
						eGFR (Estimated Glomerular Filtration Rate) הוא מדד מוערך לתפקוד
							הכליות, המראה כמה דם הכליות מסננות בדקה.
						<br />
							מחשבון זה מעריך את קצב הסינון הגלומרולרי (eGFR) בילדים ומבוגרים ע"פ
								מספר נוסחאות.
					</div>
				</div>
			)}

            <div className="inputs-container">
                <div className="form-group">
                    <label htmlFor="age_years">גיל (שנים):</label>
                    <input
                        id="age_years"
                        type="number"
                        inputMode="decimal"
                        value={age}
                        onChange={(e) => handleAgeChange(e.target.value)}
                        onBlur={handleAgeBlur}
                        placeholder="גיל בשנים"
                    />
                    {ageError && (
                        <div style={{ color: '#d32f2f', fontWeight: 'bold', marginTop: '5px', fontSize: '14px' }}>
                            {ageError}
                        </div>
                    )}
                </div>

                <div className="form-group">
                    <label htmlFor="formula_selector">
                        <div className="label-with-icon" style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                            <span>בחר נוסחה:</span>
                            <span
                                className="info-icon"
                                onClick={() => setShowInfo(p => ({ ...p, formula: !p.formula }))}
                                style={{ marginRight: '8px' }}
                            >
                                <FaInfoCircle />
                            </span>
                            <Tooltip
                                show={showInfo.formula}
                                message={`<ul style="margin: 5px 0; padding-right: 20px; text-align: right; list-style-position: inside;">
                                    <li><strong>עד גיל שנה:</strong> מומלץ להשתמש בנוסחת Schwartz</li>
                                    <li><strong>מעל גיל שנה:</strong> מומלץ להשתמש בנוסחת CKiD U25 בתנאי שהקראטינין נמדד בשיטה אנזימטית</li>
                                    <li><strong>מעל גיל 18:</strong> מומלץ להשתמש בנוסחת CKD-EPI</li>
                                </ul>`}
                            />
                        </div>
                    </label>
                    <select
                        id="formula_selector"
                        value={formula}
                        onChange={e => {
                            setFormula(e.target.value);
                            setResult(null);
                        }}
                    >
                        <option value="schwartz">Schwartz (גילאי 0-18)</option>
                        <option value="ckid">CKiD U25 (גילאי 1-25, שיטה אנזימטית)</option>
                        <option value="ckd-epi">CKD-EPI (מבוגרים 18+)</option>
                    </select>
                </div>

                {(formula === 'ckid' || formula === 'ckd-epi') && (
                    <div className="form-group">
                        <label htmlFor="gender_selector">מין:</label>
                        <select
                            id="gender_selector"
                            value={gender}
                            onChange={e => {
                                setGender(e.target.value as 'male' | 'female');
                                setResult(null);
                            }}
                        >
                            <option value="male">זכר</option>
                            <option value="female">נקבה</option>
                        </select>
                    </div>
                )}

                {formula === 'schwartz' && (
                <div className="form-group">
                    <label>
                        <div className="label-with-icon" style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                            <span>שיטת מדידת הקראטינין</span>
                            <span className="info-icon" onClick={() => setShowInfo(p => ({ ...p, method: !p.method }))} style={{ marginRight: '8px' }}>
                                <FaInfoCircle />
                            </span>
                            <Tooltip
                                show={showInfo.method}
                                message="הקבוע (K) בנוסחת שוורץ תלוי בשיטה שבה המעבדה מדדה את הקראטינין."
                            />
                        </div>
                    </label>
                    <div className="radio-group">
                        <label>
                            <input
                                type="radio"
                                value="enzymatic"
                                checked={creatinineMethod === 'enzymatic'}
                                onChange={(e) => {
                                    setCreatinineMethod(e.target.value);
                                    setResult(null);
                                }}
                            />
                            שיטה אנזימטית (Enzymatic)
                            <span className="helper-text">(K = {K_ENZYMATIC})</span>
                        </label>
                        <label>
                            <input
                                type="radio"
                                value="jaffe"
                                checked={creatinineMethod === 'jaffe'}
                                onChange={(e) => {
                                    setCreatinineMethod(e.target.value);
                                    setResult(null);
                                }}
                            />
                            שיטת Jaffe
                            <span className="helper-text">(K משתנה)</span>
                        </label>
                    </div>
                </div>
                )}

                {formula === 'schwartz' && creatinineMethod === 'jaffe' && (
                    <div className="form-group">
                        <label htmlFor="jaffe_k_selector">בחר את קבוצת המטופל (לקביעת K):</label>
                        <select id="jaffe_k_selector" value={jaffeK} onChange={e => setJaffeK(e.target.value)}>
                            <option value="0.33">פג (Preterm) (K = 0.33)</option>
                            <option value="0.45">{`תינוק עד גיל שנה (Infant < 1y) (K = 0.45)`}</option>
                            <option value="0.55">ילד/ה (1-13) או נערה (13-18) (K = 0.55)</option>
                            <option value="0.70">נער (13-18) (Adolescent Male) (K = 0.70)</option>
                        </select>
                    </div>
                )}

                {(formula === 'schwartz' || formula === 'ckid') && (
                <div className="form-group">
                    <label htmlFor="height_cm">הזן גובה (ס"מ):</label>
                    <input
                        id="height_cm"
                        type="number"
                        inputMode="decimal"
                        value={height}
                        onChange={(e) => {
                            setHeight(e.target.value);
                            resetResult();
                        }}
                        placeholder="גובה בסנטימטרים"
                    />
                </div>
                )}
                <div className="form-group">
                    <label htmlFor="creatinine_mg_dl">הזן קראטינין בדם (מ"ג/ד"ל):</label>
                    <input
                        id="creatinine_mg_dl"
                        type="number"
                        inputMode="decimal"
                        value={creatinine}
                        onChange={(e) => {
                            setCreatinine(e.target.value);
                            resetResult();
                        }}
                        placeholder="קראטינין (mg/dL)"
                    />
                </div>
                <div className="form-group">
                    <button type="button" onClick={calculateEGFR} ref={calculateButtonRef}>
                        חשב eGFR
                    </button>
                </div>
            </div>

            {result && (
                <div id="result_area" className="result-area">
                    <div className="units">הערכת קצב הסינון הכלייתי:</div>
                    <h2 style={{
                        backgroundColor: parseFloat(result.value) < 90 ? '#FDDCE1' : '#E7FFDA',
                        display: 'inline-block',
                        padding: '20px',
                        borderRadius: '16px'
                    }}>
                        eGFR = {result.value}
                    </h2>
                    <div className="units">מ"ל/דקה/1.73 מ"ר</div>
					{embedded && onApplyResult && (
						<div style={{ marginTop: 12 }}>
							<button type="button" onClick={() => onApplyResult(result.value)}>
								השתמש/י בתוצאה
							</button>
						</div>
					)}
                    {parseFloat(result.value) < 90 && (
                        <div className="units" style={{  padding: '15px', borderRadius: '8px', marginTop: '15px', textAlign: 'right', lineHeight: '1.6' }}>
                            <strong>שים/י לב:</strong> ערך eGFR נמוך מ 90.<br />
                            יש להתייעץ עם נפרולוג/ית ולשקול בירור מתאים.<br />
                            כמו כן, יש להתאים מינון תרופות ל-eGFR שהתקבל ולהימנע ככל שניתן ממתן תרופות נפרוטוקסיות.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default EGfrApp;
